import {CommonModule, KeyValue} from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    inject,
    Input,
    OnInit,
    Self,
    viewChild,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
    ReactiveFormsModule,
    FormsModule,
    ControlValueAccessor,
    FormControl,
    NgControl,
} from '@angular/forms';
import {NgbModule, NgbTypeahead} from '@ng-bootstrap/ng-bootstrap';
import {includesIgnoreCase} from '@brunata-angular/util';
import {ControlMessagesComponent} from '../control-messages/control-messages.component';
import {
    Subject,
    OperatorFunction,
    Observable,
    debounceTime,
    distinctUntilChanged,
    filter,
    merge,
    map,
} from 'rxjs';
import {v4 as uuidv4} from 'uuid';

@Component({
    selector: 'app-input-typeahead',
    templateUrl: './input-typeahead.component.html',
    styleUrls: ['./input-typeahead.component.scss'],
    imports: [CommonModule, ReactiveFormsModule, FormsModule, NgbModule, ControlMessagesComponent],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputTypeaheadComponent implements ControlValueAccessor, OnInit {
    @Input() label = '';
    @Input() labelCols = 4;
    @Input({required: true}) data?: Map<string | null, string> | string[];
    @Input() required = false;
    @Input() sortOrder = 'asc';
    @Input() filterValues = false;
    /**
     * Render the Bootstrap grid layout (label in a `col-{labelCols}`, field in the rest) instead of
     * the default stacked layout. Off by default; ngwebbill sets it to match its legacy
     * `app-input-select-type-head`.
     */
    @Input() gridLayout = false;
    /** Uppercase the value as the user types (ngwebbill's create-initials field). Off by default. */
    @Input() textUpperCase = false;
    /** Open the suggestion list when the field gains focus (ngwebbill's legacy behaviour). Off by default. */
    @Input() openOnFocus = false;
    /**
     * Style the field like a select — a dropdown chevron on the input plus tightened popup styling.
     * Off by default (plain typeahead input); ngwebbill sets it to match its legacy
     * `app-input-select-type-head` look.
     */
    @Input() selectStyle = false;

    formControl = new FormControl('');
    id: string = uuidv4();
    order: ((a: KeyValue<number, string>, b: KeyValue<number, string>) => number) | undefined;
    onTouch?: (() => void) | undefined;

    private readonly instance = viewChild.required<NgbTypeahead>('instance');
    click$ = new Subject<string>();
    focus$ = new Subject<string>();
    private onChange: ((value: string) => void) | undefined;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        @Self() public ngControl: NgControl,
        private changeDetectorRef: ChangeDetectorRef,
    ) {
        ngControl.valueAccessor = this;
    }

    get labelClass(): string {
        return `col-${this.labelCols} ${this.required ? 'required' : ''}`;
    }

    get inputColsClass(): string {
        return `col-${12 - this.labelCols}`;
    }

    onBlur(): void {
        if (this.onTouch == null) {
            return;
        }
        this.onTouch();
    }

    search: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) => {
        const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
        const clicksWithClosedPopup$ = this.click$.pipe(
            filter(() => !this.instance().isPopupOpen()),
        );
        const triggers$ = this.openOnFocus
            ? merge(debouncedText$, this.focus$, clicksWithClosedPopup$)
            : merge(debouncedText$, clicksWithClosedPopup$);
        if (this.filterValues) {
            return triggers$.pipe(
                map(term => {
                    const values = this.dataValues();
                    return term === '' && values.length > 0
                        ? values
                        : values.filter(v => includesIgnoreCase(v, term)).slice(0, 10);
                }),
            );
        } else {
            return triggers$.pipe(map(() => this.dataValues()));
        }
    };

    private dataValues(): string[] {
        // Works for both Map<…, string> and string[] — both expose values().
        return Array.from(this.data?.values() ?? []);
    }

    ngOnInit(): void {
        this.formControl.valueChanges.subscribe(data => {
            if (this.onChange == null) {
                return;
            }
            this.onChange(data ?? '');
            if (!this.formControl.valid) {
                this.ngControl.control?.setErrors(this.formControl.errors);
            }
            if (this.textUpperCase && data != null) {
                this.formControl.setValue(data.toUpperCase(), {emitEvent: false});
            }
        });
        if (this.sortOrder === 'asc') {
            this.order = this.orderAsc;
        } else if (this.sortOrder === 'dsc') {
            this.order = this.orderDsc;
        } else if (this.sortOrder === 'keys') {
            this.order = this.orderKeys;
        } else {
            this.order = this.orderOriginal;
        }

        // Then using ChangeDetectionStrategy.OnPush the component doesn't notice, when the control is marked as
        // touched, and will therefore not show validation errors. However, Angular doesn't offer a simple way of
        // listening to, when a control is marked as touched, that we can hook into. So we have to reassign the
        // markAsTouched() method in order to call detectChanges().
        if (this.ngControl.control != null) {
            const originalMarkAsTouched = this.ngControl.control.markAsTouched.bind(
                this.ngControl.control,
            );
            const changeDetectorRef = this.changeDetectorRef;
            this.ngControl.control.markAsTouched = function (...args): void {
                originalMarkAsTouched.apply(this, args);
                changeDetectorRef.detectChanges();
            };
        }

        // An async validator settles after the blur has rendered PENDING, and nothing else marks
        // this OnPush view then, so its error would stay hidden until the next blur or submit.
        this.ngControl.control?.statusChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.changeDetectorRef.markForCheck());
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouch = fn;
    }

    writeValue(value: string): void {
        this.formControl.setValue(value, {emitEvent: false});
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.formControl.disable();
        } else {
            this.formControl.enable();
        }
    }

    orderAsc = (a: KeyValue<number, string>, b: KeyValue<number, string>): number =>
        a.value.toUpperCase() > b.value.toUpperCase()
            ? 1
            : a.value.toUpperCase() < b.value.toUpperCase()
              ? -1
              : 0;

    orderDsc = (a: KeyValue<number, string>, b: KeyValue<number, string>): number =>
        a.value.toUpperCase() > b.value.toUpperCase()
            ? -1
            : a.value.toUpperCase() < b.value.toUpperCase()
              ? 1
              : 0;

    orderKeys = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => a.key - b.key;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    orderOriginal = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => 0;
}
